import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as brandingService from '@/services/branding.service'
import type { TenantBrandingPatch } from '@/services/branding.service'
import { resolveBrandingAssetUrl, publicBrandingLogoUrl, DEFAULT_BRAND_LOGO } from '@/utils/branding'
import { apiV1Url } from '@/utils/apiBase'

/** The app short name is not editable here: it is fixed after provisioning. */
export type CompanyDetailsInput = {
  company_name: string
  company_address: string
  company_city: string
  company_state: string
  company_postal_code: string
  company_country: string
  company_cr: string
  company_vat: string
}

export const useBrandingStore = defineStore('branding', () => {
  const logoUrl = ref<string | null>(null)
  /** Anonymous-safe logo URL, set only by the pre-auth host lookup. */
  const logoPublicUrl = ref<string | null>(null)
  const companyName = ref('')
  /** Uppercase abbrev shown under the installed app icon (Empty = product name). */
  const shortName = ref('')
  const companyLocation = ref('')
  const companyAddress = ref('')
  const companyCity = ref('')
  const companyState = ref('')
  const companyPostalCode = ref('')
  const companyCountry = ref('')
  const companyCr = ref('')
  const companyVat = ref('')
  const loading = ref(false)
  const error = ref<string | null>(null)

  const hasLogo = computed(() => !!logoUrl.value?.trim())

  /** Use for `<img src>` — fixes API host when DB has a different origin than the app. */
  const displayLogoUrl = computed(
    () => logoPublicUrl.value || resolveBrandingAssetUrl(logoUrl.value),
  )

  /** Sidebar/header logo: tenant image when set, otherwise platform default. */
  const sidebarLogoUrl = computed(() => displayLogoUrl.value || DEFAULT_BRAND_LOGO)

  const usesCustomLogo = computed(() => !!displayLogoUrl.value)

  function applyBranding(data: brandingService.TenantBrandingData) {
    const u = data.logo_url?.trim()
    logoUrl.value = u || null
    // Authenticated branding resolved: drop the pre-auth public URL so the asset route
    // (still template:read-gated) stays the source of truth for signed-in shells.
    logoPublicUrl.value = null
    companyName.value = data.company_name?.trim() || ''
    shortName.value = data.short_name?.trim() || ''
    companyLocation.value = data.company_location?.trim() || ''
    companyAddress.value = data.company_address?.trim() || ''
    companyCity.value = data.company_city?.trim() || ''
    companyState.value = data.company_state?.trim() || ''
    companyPostalCode.value = data.company_postal_code?.trim() || ''
    companyCountry.value = data.company_country?.trim() || ''
    companyCr.value = data.company_cr?.trim() || ''
    companyVat.value = data.company_vat?.trim() || ''
  }

  function resetCompanyFields() {
    companyName.value = ''
    shortName.value = ''
    companyLocation.value = ''
    companyAddress.value = ''
    companyCity.value = ''
    companyState.value = ''
    companyPostalCode.value = ''
    companyCountry.value = ''
    companyCr.value = ''
    companyVat.value = ''
  }

  async function fetchPublicHostBranding() {
    const hostname = window.location.hostname.trim().toLowerCase()
    if (!hostname) {
      logoUrl.value = null
      logoPublicUrl.value = null
      resetCompanyFields()
      return
    }
    const res = await fetch(
      apiV1Url(`/public/workspace-status?host=${encodeURIComponent(hostname)}`),
      { credentials: 'omit' },
    )
    if (res.ok) {
      const data = (await res.json()) as {
        logo_url?: string | null
        logo_public_url?: string | null
        company_name?: string | null
        short_name?: string | null
      }
      logoUrl.value = data.logo_url?.trim() || null
      const apiPublicLogo = data.logo_public_url?.trim()
      // Normalise through the same helper as the stored asset URL: the API may return a
      // relative path while VITE_API_BASE_URL points at another origin.
      logoPublicUrl.value = apiPublicLogo
        ? resolveBrandingAssetUrl(apiPublicLogo)
        : logoUrl.value
          ? publicBrandingLogoUrl(hostname)
          : null
      companyName.value = data.company_name?.trim() || ''
      shortName.value = data.short_name?.trim() || ''
      return
    }
    logoUrl.value = null
    logoPublicUrl.value = null
    resetCompanyFields()
  }

  async function fetchDisplayBranding() {
    loading.value = true
    error.value = null
    try {
      try {
        const data = await brandingService.getTenantBranding()
        applyBranding(data)
        return
      } catch {
        // Fall back to host-based public lookup (login / pre-auth hosts).
      }
      await fetchPublicHostBranding()
    } catch {
      logoUrl.value = null
      logoPublicUrl.value = null
      resetCompanyFields()
    } finally {
      loading.value = false
    }
  }

  async function fetchFullBranding() {
    loading.value = true
    error.value = null
    try {
      const data = await brandingService.getTenantBranding()
      applyBranding(data)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load branding'
      try {
        await fetchPublicHostBranding()
      } catch {
        logoUrl.value = null
        logoPublicUrl.value = null
        resetCompanyFields()
      }
    } finally {
      loading.value = false
    }
  }

  async function fetchBranding() {
    return fetchFullBranding()
  }

  function clear() {
    logoUrl.value = null
    logoPublicUrl.value = null
    resetCompanyFields()
    error.value = null
  }

  async function saveLogoUrl(url: string | null) {
    const data = await brandingService.patchTenantBranding({
      logo_url: url ?? '',
    })
    applyBranding(data)
  }

  async function saveCompanyDetails(details: CompanyDetailsInput) {
    // short_name is left out on purpose: the API only updates fields present in a PATCH,
    // so the fixed value stays as it is, even if support changed it since this form opened.
    const body: TenantBrandingPatch = {
      company_name: details.company_name,
      company_address: details.company_address,
      company_city: details.company_city,
      company_state: details.company_state,
      company_postal_code: details.company_postal_code,
      company_country: details.company_country,
      company_cr: details.company_cr,
      company_vat: details.company_vat,
    }
    const data = await brandingService.patchTenantBranding(body)
    applyBranding(data)
  }

  return {
    logoUrl,
    logoPublicUrl,
    displayLogoUrl,
    sidebarLogoUrl,
    usesCustomLogo,
    companyName,
    shortName,
    companyLocation,
    companyAddress,
    companyCity,
    companyState,
    companyPostalCode,
    companyCountry,
    companyCr,
    companyVat,
    loading,
    error,
    hasLogo,
    fetchBranding,
    fetchDisplayBranding,
    fetchFullBranding,
    clear,
    saveLogoUrl,
    saveCompanyDetails,
  }
})
